import React from 'react';
import PropTypes from 'prop-types';
import { withCookies, Cookies } from 'react-cookie';
import { Route, Switch } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import moment from 'moment';
import CookieConsent from 'react-cookie-consent';
import axios from 'axios';
import StripeService from './services/stripeService';
import Certificate from './sections/certificate/Certificate';
import RenderService from './services/render';
import UserProp from './commonComponents/propTypes/userProp';
import BrandingProp from './commonComponents/propTypes/brandingProp';
import TenantConfigsProp from './commonComponents/propTypes/tenantConfigsProp';
import TranslationContext from './contextProviders/TranslationContext';
import CSRFContext from './contextProviders/CSRFContext';
import IsAcademyPublicContext from './contextProviders/IsAcademyPublicContext';
import LoggedUserContext from './contextProviders/LoggedUserContext';
import MetaContext from './contextProviders/MetaContext';
import ServicesContext from './contextProviders/ServicesContext';
import LanguagePrefixContext from './contextProviders/LanguagePrefixContext';
import MarketInfosContext from './contextProviders/MarketInfosContext';
import ShopCartContext from './contextProviders/ShopCartContext';
import { BadgeContextProvider } from './contextProviders/BadgeContextProvider';
import Title from './commonComponents/Title';
import Header from './commonComponents/pageLayout/header/Header';
import Footer from './commonComponents/pageLayout/Footer';
import SocketClient from './services/socketClient';
import ChatService from './services/chatService';
import LandingPage from './sections/landing/Landing';
import AuthorizedNavigation from './services/authorizedNavigation';
import LoginModal from './sections/signIn/modals/LoginModal';
import TawkTo from './services/tawkTo';
import NotFound from './sections/error/NotFound';
import Profile from './sections/profile/ProfileWrapper';
import Plan from './sections/plan/MyPlanPage';
import SignIn from './sections/signIn/SignIn';
import SignUp from './sections/signUp/SignUp';
import Error from './sections/error/Error';
import Perform from './sections/challenges/Perform';
import Dashboard from './sections/inbox/Dashboard';
import Academy from './sections/academy/AcademyWrapper';
import AcademyDetails from './sections/academy/AcademyDetails';
import ChallengeDetails from './sections/challenges/ChallengeDetailsWrapper';
import LeadCreate from './sections/challenges/LeadCreate';
import LeadDetails from './sections/challenges/LeadDetails';
import RecommendationDetails from './sections/challenges/RecommendationDetails';
import InboxCreate from './sections/inbox/InboxCreate';
import Survey from './sections/survey/SurveyWrapper';
import JourneyOverall from './sections/journey/JourneyOverall';
import CountryProp from './commonComponents/forms/Address/CountryProp';
import AcademyConfigProp from './sections/landing/propTypes/academyConfig';
import CurrencyProp from './sections/profile/propTypes/currencyProp';
import ReportAcademyScorm from './sections/reports/academy/ReportAcademyScorm';
import TenantProp from './commonComponents/propTypes/tenantProp';
import ShopMain from './sections/shop/ShopMain';
import ShopSearch from './sections/shop/ShopSearch';
import ShopProductPage from './sections/shop/ShopProductPage';
import ShopCheckoutSteps from './sections/shop/ShopCheckoutSteps';
import ShopUserOrders from './sections/shop/ShopUserOrders';
import ShopUserOrderPage from './sections/shop/ShopUserOrderPage';
import ShopUserSubscriptionsOrderPage from './sections/shop/ShopUserSubscriptionsOrderPage';
import OrdersList from './sections/orders/OrdersList';
import SessionDetails from './sections/sessions/SessionDetails';
import SessionDetailsPublic from './sections/sessions/SessionDetailsPublic';
import SessionWrapper from './sections/sessions/SessionWrapper';
import MySessions from './sections/sessions/MySessionWrapper';
import JourneysAcademyWrapper from './sections/journey/JourneysAcademyWrapper';
import SessionCreateEditWrapper from './sections/sessions/SessionCreateEditWrapper';
import ShopUserOrderPay from './sections/shop/ShopUserOrderPay';
import Terms from './sections/terms/Terms';
import PrivacyPolicy from './sections/privacy-policy/PrivacyPolicy';
import DSKBankPrivacyPolicy from './sections/privacyAndTerms/DSKBankPrivacyPolicy';
import RecoverPasswordForm from './sections/signIn/RecoverPasswordForm';
import OrderCreate from './sections/orders/OrderCreate';
import CommissionsDashboard from './sections/commissions/CommissionsDashboard';
import ReportPerform from './sections/reports/perform/ReportPerform';
import TeamStructure from './sections/teams/TeamStructure';
import MarketUserPage from './sections/market/MarketUserPage';
import Notifications from './services/notifications';
import i18n from './services/i18n';
import MarketInfosModal from './commonComponents/modal/MarketInfosModal';
import DirectSellingModelContext from './contextProviders/DirectSellingModelContext';
import CompleteRegistration from './sections/completeRegistration/CompleteRegistration';
import MobileAppBanner from './commonComponents/banner/MobileAppBanner';
import CancellationPolicy from './sections/privacyAndTerms/CancellationPolicy';
import SubscriptionPlanModal from './sections/signIn/modals/SubscriptionPlanModal/SubscriptionPlanModal';
import UserPortalMenusProp from './commonComponents/propTypes/userPortalMenusProp';
import ConfirmationPlanModal from './sections/signIn/modals/SubscriptionPlanModal/ConfirmationPlanModal';
import ConfigsContext from './contextProviders/ConfigsContext';
import organizationProp from './commonComponents/propTypes/organizationProp';
import HeadBackground from './commonComponents/pageLayout/HeadBackground';
import { encodeString } from './services/utilityFunctions';
import TeamReport from './sections/reports/tailor-made/neofin/TeamReport';
import ArcovaMyCareer from './sections/reports/tailor-made/arcova/MyCareer';
import NeofinMyCareer from './sections/reports/tailor-made/neofin/MyCareer';
import MivitanaMyCareer from './sections/reports/tailor-made/mivitana/MyCareer';
import LiveObenMyCareer from './sections/reports/tailor-made/liveoben/MyCareer';
import LabOriginMyCareer from './sections/reports/tailor-made/laborigin/MyCareer';
import BonusListPage from './sections/reports/tailor-made/pureliink/BonusListPage';
import WorldContext from './contextProviders/WorldContext';
import VatTypesProp from './commonComponents/forms/Address/VatTypesProp';
import OpportunitiesList from './sections/opportunities/OpportunitiesList';
import Opportunity from './sections/opportunities/Opportunity';
import SelectSponsorModal from './commonComponents/modal/SelectSponsorModal';
import MyAgenda from './sections/my-agenda/MyAgenda';
import IsTestModeContext from './contextProviders/IsTestModeContext';

/**
 * Wraps a component in the default page layout, header/footer, etc
 */
// Pages With market modal off (Sign In, password, user/registration recover)
const pagesWithMarketModalOff = [
  /^(\/[a-z]{2})?\/signin$/i,
  /^(\/[a-z]{2})?\/account\/create$/i,
  /^(\/[a-z]{2})?\/password\/recover\/[a-zA-Z0-9-_]+$/i,
  /^(\/[a-z]{2})?\/user\/registration\/[a-zA-Z0-9-_]+$/i,
  /^(\/[a-z]{2})?\/privacy-policy$/i,
  /^(\/[a-z]{2})?\/terms-and-conditions$/i,
  /^(\/[a-z]{2})?\/cancellation-policy$/i,
];

const pagesWithForcedPublicRegistration = [
  /^(\/[a-z]{2})?\/user\/[a-zA-Z0-9-_]+$/i,
];

function wrapInDefaultLayout(Component, props) {
  const { theme, profile, version, branding, ...other } = props;
  const {
    languages,
    availableCurrencies,
    authOrganizations,
    selectedOrganizationId,
    availableCountries,
    bravonLanguages,
    userPortalMenus,
    pageBodyClass,
  } = other || {};
  const tenant = profile ? profile.tenant : null;
  const logo = branding ? branding.horizontalLogo : undefined;

  return function (routeProps) {
    const withRoute = { ...other, ...routeProps };
    return (
      <>
        <Header
          theme={theme}
          profile={profile}
          languages={languages}
          availableCurrencies={availableCurrencies}
          authOrganizations={authOrganizations}
          selectedOrganizationId={selectedOrganizationId}
          logo={logo}
          availableCountries={availableCountries}
          bravonLanguages={bravonLanguages}
          userPortalMenus={userPortalMenus}
        />
        <HeadBackground branding={branding} pageBodyClass={pageBodyClass}>
          <Component {...withRoute} />
        </HeadBackground>
        <Footer version={version} tenant={tenant} />
      </>
    );
  };
}

/**
 * Renders the error page, using the error received from the server, or a client side error
 */
function renderError(props, clientSideError) {
  const finalProps = {
    ...props,
    ...clientSideError,
    status: props.status || 500,
  };
  return <Error {...finalProps} />;
}

/**
 * React app entry point, creates context providers and an error boundary and
 * wraps most of the pages in a Header/Footer layout, as needed.
 */
class Main extends React.Component {
  constructor(props) {
    super(props);
    const marketInfos = props.marketInfosCookies;
    // Making sure that market infos modal is shown if user has not configured
    // shipping country and currency before
    if (props.profile) {
      if (!props.profile.shippingCountry) {
        marketInfos.localCountry = '';
      }
      if (!props.profile.currency) {
        marketInfos.localCurrency = '';
      }
    }
    this.state = {
      hasError: false,
      error: undefined,
      modalInfo: { show: false, redirectLink: '' },
      plansModal: { show: false },
      successPlanModal: { plan: null, show: false },
      marketInfosModal: { show: false, data: null },
      loggedUser: props.profile,
      marketInfos,
      // shopCart: null,
    };

    this.handleModalClose = this.handleModalClose.bind(this);
    this.handlePlansModalClose = this.handlePlansModalClose.bind(this);
    this.handleMarketInfosModalClose =
      this.handleMarketInfosModalClose.bind(this);
    this.handleUpdateLoggedUser = this.handleUpdateLoggedUser.bind(this);
    this.handleUpdateShopCart = this.handleUpdateShopCart.bind(this);
    this.handleMarketInfos = this.handleMarketInfos.bind(this);
  }

  componentDidMount() {
    const {
      stripe,
      profile,
      socketUrl,
      _csrf,
      favicon,
      tawkToKey,
      orgServices,
      directSellingActive,
      marketInfosCookies,
      publicModules,
      languages,
      url,
      organizationConfigs,
    } = this.props;
    const { organization } = organizationConfigs;
    const {
      _id: userId,
      tenant,
      role,
      currency,
      language,
      shippingCountry,
    } = profile || {};
    if (profile) {
      if (currency && currency !== marketInfosCookies.localCurrency) {
        this.handleMarketInfos('localCurrency', currency);
      }
      if (
        shippingCountry &&
        shippingCountry !== marketInfosCookies.localCountry
      ) {
        this.handleMarketInfos('localCountry', shippingCountry);
      }
      if (language && language !== marketInfosCookies.localLanguage) {
        this.handleMarketInfos('localLanguage', language);
      }
    }
    // Initiate necessary services
    // FIXME : implement the chat sidebar in react and remove all of these
    // unnecessary jquery/ejs based features
    const chatDisabled =
      orgServices &&
      orgServices.connect &&
      orgServices.connect.enabled === false;
    if (userId && !chatDisabled) {
      const usersDisabled =
        orgServices.connect &&
        orgServices.connect.config &&
        orgServices.connect.config.usersEnabled === false;
      const groupsDisabled =
        orgServices.connect &&
        orgServices.connect.config &&
        orgServices.connect.config.groupsEnabled === false;

      RenderService.init(role);
      ChatService.init(
        userId,
        _csrf,
        !usersDisabled,
        !groupsDisabled,
        directSellingActive,
        languages,
        tenant,
        organization,
      );
    }

    // Setup Socket Connection (chat service and real update in pages)
    if (userId) {
      SocketClient.init(socketUrl, userId, organization, tenant._id);
    }

    // Setup Stripe
    StripeService.init(stripe || {});

    const hasPublicAccess =
      publicModules?.academy || publicModules?.marketPlace;
    const hasForcedPublicRegistration = pagesWithForcedPublicRegistration.some(
      regex => regex.test(url),
    );

    // Authorized Navigation Initialization
    AuthorizedNavigation.init(!!userId, tenant);
    AuthorizedNavigation.on('blocked', redirectLink => {
      if (hasPublicAccess || hasForcedPublicRegistration) {
        this.setState({ modalInfo: { show: true, redirectLink } });
      } else {
        window.location.href = `/signin${
          redirectLink ? `?redirect=${encodeString(redirectLink)}` : ''
        }`;
      }
    });

    // Setup TawkTo
    const tawkTo = new TawkTo(tawkToKey, profile);
    tawkTo.init();

    // Setup favicon
    const $favicon = document.querySelector('link[rel="shortcut icon"]');
    if ($favicon !== null) {
      $favicon.href = favicon;
    }

    // Add animateClass to jQuery
    $.fn.animateClass = function animateClass(cls, cbParam) {
      let cb = cbParam;
      if (typeof cbParam === 'string') {
        const endClass = cbParam;
        cb = function addJqueryClass() {
          $(this).addClass(endClass);
        };
      }

      return $(this)
        .one('webkitAnimationEnd', function animationEnd() {
          $(this).removeClass(cls);
          if (cb) cb.call(this);
        })
        .addClass(cls);
    };
    // if (this.state.shopCart === null) {
    //   const shopCart = this.props.cookies.get('shopCart') || [];
    //   // this.setState({ shopCart });
    // }

    const $burgerMobileMenu = $('#m_aside_header_menu_mobile_toggle');
    if ($burgerMobileMenu) {
      $burgerMobileMenu.click(() => {
        const $menu = $('#m_header_menu');
        if ($menu.hasClass('m-aside-header-menu-mobile--on')) {
          $menu.removeClass('m-aside-header-menu-mobile--on');
        } else {
          $menu.addClass('m-aside-header-menu-mobile--on');
        }
      });
    }

    // It will check by regex the white listed urls so we dont show the popup if the user is not logged it that cases
    const marketModalOff = pagesWithMarketModalOff.reduce(
      (hasMatch, regex) => hasMatch || regex.test(url),
      false,
    );
    const userPlanModalOff = pagesWithMarketModalOff.reduce(
      (hasMatch, regex) => hasMatch || regex.test(url),
      false,
    );
    const isErrorPage =
      url === '404error' ||
      url === '401error' ||
      url === '403error' ||
      url === '500error';
    if (
      !isErrorPage &&
      !profile &&
      !marketModalOff &&
      (!marketInfosCookies.localCountry || !marketInfosCookies.localCurrency)
    ) {
      this.setState({ marketInfosModal: { show: true, data: null } });
    }
    if (
      !isErrorPage &&
      profile &&
      (!profile.shippingCountry || !profile.currency)
    ) {
      this.setState({ marketInfosModal: { show: true, data: null } });
    }
    let showUserPlansModal = false;
    if (profile && profile?.role === UserProp.ROLES.USER) {
      showUserPlansModal =
        profile &&
        profile.organizationHasUserPlans &&
        (!profile.userPlanSubscription ||
          profile?.userPlanSubscription?.status !== 'active');
    }
    if (
      !isErrorPage &&
      !userPlanModalOff &&
      marketInfosCookies.localCountry &&
      marketInfosCookies.localCurrency &&
      showUserPlansModal
    ) {
      this.setState({ plansModal: { show: showUserPlansModal } });
    }
  }

  componentDidUpdate() {
    const $burgerMobileMenu = $('#m_aside_header_menu_mobile_toggle');
    if ($burgerMobileMenu) {
      $burgerMobileMenu.click(() => {
        const $menu = $('#m_header_menu');
        if ($menu.hasClass('m-aside-header-menu-mobile--on')) {
          $menu.removeClass('m-aside-header-menu-mobile--on');
        } else {
          $menu.addClass('m-aside-header-menu-mobile--on');
        }
      });
    }
  }

  componentDidCatch(error, info) {
    this.setState({
      hasError: true,
      error: { message: error.toString(), stack: info.componentStack },
    });
  }

  handleModalClose() {
    this.setState({ modalInfo: { show: false, redirectLink: '' } });
  }

  handlePlansModalClose(plan) {
    this.setState({
      plansModal: { show: false },
      successPlanModal: { plan, show: true },
    });
  }

  handleMarketInfosModalClose(profile, reloadAfterCookies = false) {
    if (!this.state.marketInfos.localCountry) {
      Notifications.showNotificationError(
        i18n.__('error'),
        i18n.__('modal.notLogged.error.country'),
      );
      return;
    }
    if (!this.state.marketInfos.localCurrency) {
      Notifications.showNotificationError(
        i18n.__('error'),
        i18n.__('modal.notLogged.error.currency'),
      );
      return;
    }
    let showUserPlansModal = false;
    if (profile && profile?.role === UserProp.ROLES.USER) {
      showUserPlansModal =
        profile &&
        profile.organizationHasUserPlans &&
        (!profile.userPlanSubscription ||
          profile?.userPlanSubscription?.status !== 'active');
    }
    if (showUserPlansModal || reloadAfterCookies) {
      window.location.reload();
      Notifications.showNotificationWarning(
        i18n.__('warning'),
        i18n.__('warning.updating.preferences'),
      );
    } else {
      this.setState({ marketInfosModal: { show: false, data: null } });
    }
  }

  handleUpdateLoggedUser(loggedUser) {
    this.setState({ loggedUser });
  }

  handleUpdateShopCart(cart) {
    const { cookies } = this.props;
    const urlElements = window.location.hostname.split('.');
    let domain = window.location.hostname;
    if (urlElements.length > 2) {
      domain = `.${urlElements[urlElements.length - 2]}.${
        urlElements[urlElements.length - 1]
      }`;
    }
    cookies.set('shopCart', cart, { domain, path: '/' });
    // this.setState({ shopCart: cart });
  }

  async handleMarketInfos(type, value, user, _csrf) {
    this.setState(prevState => ({
      ...prevState,
      marketInfos: {
        ...prevState.marketInfos,
        [type]: value,
      },
    }));
    const { cookies } = this.props;
    const urlElements = window.location.hostname.split('.');
    let domain = window.location.hostname;
    if (urlElements.length > 2) {
      domain = `.${urlElements[urlElements.length - 2]}.${
        urlElements[urlElements.length - 1]
      }`;
    }
    if (user && (!user.shippingCountry || !user.currency)) {
      const field = type === 'localCountry' ? 'shippingCountry' : 'currency';
      await axios
        .put(
          '/api/user/me/settings',
          { [field]: value },
          {
            headers: {
              'Accept-Version': '1.0.0',
              'X-Gamification-API': 'Organization',
              'X-CSRF-Token': _csrf,
              _csrf,
            },
          },
        )
        .then(({ data: response }) => {
          this.handleUpdateLoggedUser(response.data);
        })
        .catch(err => {
          Notifications.showErrorFromRequest(err, 'error_profileUpdate');
        });
    }
    const expirationDate = moment().add(2, 'year');
    cookies.set(type, value, {
      domain,
      path: '/',
      maxAge: expirationDate.valueOf(),
    });
  }

  render() {
    const {
      t,
      _csrf,
      branding,
      academyConfig,
      orgServices,
      languages,
      languagePrefix,
      meta,
      share,
      configs,
      publicModules,
      hasAcademyHeader,
      isShopPage,
      stripe,
      countries,
      currencies,
      isSignIn,
      directSellingModel,
      cookies,
      tenant,
      userPortalMenus,
      organizationConfigs,
      availableCurrencies,
      availableCountries,
      vatTypes,
      reloadAfterCookies,
      isToOpenSponsorModal,
      isTestMode,
      isTrial,
    } = this.props;
    const {
      hasError,
      error,
      modalInfo,
      plansModal,
      loggedUser,
      marketInfos,
      marketInfosModal,
      successPlanModal,
    } = this.state;
    const shopCart = this.props.cookies.get('shopCart') || [];
    const stripePromise = loadStripe(stripe.key);
    const isLogged = loggedUser !== null;
    // Fix the url redirection if user is not logged when 'en' put ''
    const marketLanguageToUse =
      marketInfos?.localLanguage === 'en' ? '' : marketInfos.localLanguage;
    const languagePrefixToUse = languagePrefix === 'en' ? '' : languagePrefix;
    const showMarketInfosModal =
      !isSignIn &&
      marketInfosModal.show &&
      !(availableCurrencies.length === 1 && availableCountries.length === 1); // if only one each don't show

    const { organization, tenant: tenantConfigs } = organizationConfigs;

    const organizationTailorMadeReports =
      organization?.tailorMadeAdminReports || [];
    const tenantTailorMadeReports = tenantConfigs?.tailorMadeAdminReports || [];

    const getCareerPage = () => {
      const availableCarreerPages = [
        'arcova_my_career',
        'neofin_my_career',
        'mivitana_my_career',
        'liveoben_my_career',
        'laborigin_my_career',
      ];

      let careerPage = 'arcova_my_career';
      for (const page of availableCarreerPages) {
        if (
          organizationTailorMadeReports.includes(page) ||
          tenantTailorMadeReports.includes(page)
        ) {
          // We take the first one we find
          careerPage = page;
          break;
        }
      }

      switch (careerPage) {
        case 'arcova_my_career':
          return ArcovaMyCareer;
        case 'neofin_my_career':
          return NeofinMyCareer;
        case 'mivitana_my_career':
          return MivitanaMyCareer;
        case 'liveoben_my_career':
          return LiveObenMyCareer;
        case 'laborigin_my_career':
          return LabOriginMyCareer;
        default:
          return ArcovaMyCareer;
      }
    };

    return (
      <TranslationContext.Provider value={t}>
        <CSRFContext.Provider value={_csrf}>
          <IsAcademyPublicContext.Provider value={hasAcademyHeader}>
            <ShopCartContext.Provider
              value={{ shopCart, setShopCart: this.handleUpdateShopCart }}
            >
              <ConfigsContext.Provider value={organizationConfigs}>
                <LoggedUserContext.Provider
                  value={{
                    loggedUser,
                    setLoggedUser: this.handleUpdateLoggedUser,
                  }}
                >
                  <MetaContext.Provider
                    value={{
                      meta,
                      share,
                      configs,
                      publicModules,
                      branding,
                      userPortalMenus,
                    }}
                  >
                    <ServicesContext.Provider value={orgServices}>
                      <LanguagePrefixContext.Provider
                        value={
                          loggedUser ? languagePrefixToUse : marketLanguageToUse
                        }
                      >
                        <MarketInfosContext.Provider
                          value={{
                            marketInfos,
                            setMarketInfos: this.handleMarketInfos,
                          }}
                        >
                          <DirectSellingModelContext.Provider
                            value={directSellingModel}
                          >
                            <WorldContext.Provider
                              value={{
                                languages,
                                countries: availableCountries,
                                currencies: availableCurrencies,
                                vatTypes,
                              }}
                            >
                              <IsTestModeContext.Provider
                                value={{
                                  isTestMode,
                                  isTrial,
                                }}
                              >
                                <BadgeContextProvider>
                                  <Elements stripe={stripePromise}>
                                    <Title>
                                      <>
                                        <MobileAppBanner
                                          meta={meta}
                                          language={marketLanguageToUse}
                                          cookies={cookies}
                                          tenant={tenant}
                                        />
                                        {modalInfo.show && (
                                          <LoginModal
                                            t={t}
                                            tenant={tenant}
                                            _csrf={_csrf}
                                            countries={countries}
                                            redirectLink={
                                              modalInfo.redirectLink
                                            }
                                            logo={
                                              academyConfig
                                                ? academyConfig.footerLogo
                                                : undefined
                                            }
                                            languages={languages}
                                            onClose={this.handleModalClose}
                                            cookies={cookies}
                                          />
                                        )}
                                        {successPlanModal.show &&
                                          successPlanModal.plan && (
                                            <ConfirmationPlanModal
                                              t={t}
                                              plan={successPlanModal.plan}
                                              onClose={() => {
                                                window.location.reload();
                                              }}
                                            />
                                          )}
                                        {plansModal.show && (
                                          <SubscriptionPlanModal
                                            t={t}
                                            _csrf={_csrf}
                                            tenant={tenant}
                                            countries={countries}
                                            setHasPlans={() => {}}
                                            onPlansModalClose={
                                              this.handlePlansModalClose
                                            }
                                          />
                                        )}
                                        {showMarketInfosModal && (
                                          <MarketInfosModal
                                            marketInfos={marketInfos}
                                            countries={availableCountries}
                                            currencies={availableCurrencies}
                                            onClose={() =>
                                              this.handleMarketInfosModalClose(
                                                loggedUser,
                                                reloadAfterCookies,
                                              )
                                            }
                                            onChange={(field, value) =>
                                              this.handleMarketInfos(
                                                field,
                                                value,
                                                loggedUser,
                                                _csrf,
                                              )
                                            }
                                          />
                                        )}
                                        {isToOpenSponsorModal && (
                                          <SelectSponsorModal />
                                        )}
                                        <CookieConsent
                                          location="bottom"
                                          buttonText={t('cookies.button')}
                                          cookieName="cookieAcceptance"
                                          style={{
                                            background: '#2B373B',
                                            paddingLeft: '6rem',
                                          }}
                                          buttonId="acceptCookies"
                                          acceptOnOverlayClick
                                        >
                                          {t('cookies.message')}
                                        </CookieConsent>
                                        {hasError || this.props.error ? (
                                          <>
                                            {renderError(this.props, { error })}
                                          </>
                                        ) : (
                                          <Switch>
                                            <Route
                                              path="/:lang([a-z]{2})?"
                                              component={({ match }) => (
                                                <Switch>
                                                  <Route
                                                    path={`${match.url}/signin`}
                                                    render={() => (
                                                      <SignIn
                                                        {...this.props}
                                                        logo={
                                                          branding
                                                            ? branding.horizontalLogo
                                                            : undefined
                                                        }
                                                      />
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/account/create`}
                                                    render={() => (
                                                      <SignUp
                                                        {...this.props}
                                                        logo={
                                                          branding
                                                            ? branding.horizontalLogo
                                                            : undefined
                                                        }
                                                      />
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/user/registration`}
                                                    render={() => (
                                                      <CompleteRegistration
                                                        {...this.props}
                                                        logo={
                                                          branding
                                                            ? branding.horizontalLogo
                                                            : undefined
                                                        }
                                                      />
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/password/recover/:token`}
                                                    render={() => (
                                                      <RecoverPasswordForm
                                                        {...this.props}
                                                      />
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/privacy-policy`}
                                                    render={wrapInDefaultLayout(
                                                      PrivacyPolicy,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/terms-and-conditions`}
                                                    render={wrapInDefaultLayout(
                                                      Terms,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/cancellation-policy`}
                                                    render={wrapInDefaultLayout(
                                                      CancellationPolicy,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    exact
                                                    path={`${match.url}/plan`}
                                                    render={wrapInDefaultLayout(
                                                      Plan,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/profile`}
                                                    render={wrapInDefaultLayout(
                                                      Profile,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/profile`}
                                                    render={wrapInDefaultLayout(
                                                      Profile,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    exact
                                                    path={`${match.url}/dashboard`}
                                                    render={wrapInDefaultLayout(
                                                      Dashboard,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/dashboard/create`}
                                                    render={wrapInDefaultLayout(
                                                      InboxCreate,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/academy/journeys`}
                                                    render={wrapInDefaultLayout(
                                                      JourneysAcademyWrapper,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/academy/my-sessions`}
                                                    render={wrapInDefaultLayout(
                                                      MySessions,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/academy/session/create`}
                                                    render={wrapInDefaultLayout(
                                                      SessionCreateEditWrapper,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/academy/session/edit/:slug`}
                                                    render={wrapInDefaultLayout(
                                                      SessionCreateEditWrapper,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/academy/sessions/:slug/verify/participation`}
                                                    render={wrapInDefaultLayout(
                                                      SessionDetails,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/academy/sessions/:slug`}
                                                    render={
                                                      isLogged
                                                        ? wrapInDefaultLayout(
                                                            SessionDetails,
                                                            this.props,
                                                          )
                                                        : wrapInDefaultLayout(
                                                            SessionDetailsPublic,
                                                            this.props,
                                                          )
                                                    }
                                                  />
                                                  <Route
                                                    path={`${match.url}/wallet`}
                                                    render={wrapInDefaultLayout(
                                                      CommissionsDashboard,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/my-career`}
                                                    render={wrapInDefaultLayout(
                                                      getCareerPage(),
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/my-agenda`}
                                                    render={wrapInDefaultLayout(
                                                      MyAgenda,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/reports/bonus-list`}
                                                    render={wrapInDefaultLayout(
                                                      BonusListPage,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/academy/sessions`}
                                                    render={wrapInDefaultLayout(
                                                      SessionWrapper,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    exact
                                                    path={`${match.url}/academy`}
                                                    render={wrapInDefaultLayout(
                                                      Academy,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/academy/:id`}
                                                    render={wrapInDefaultLayout(
                                                      AcademyDetails,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    exact
                                                    path={`${match.url}/challenges`}
                                                    render={wrapInDefaultLayout(
                                                      Perform,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/challenges/leads/create`}
                                                    render={wrapInDefaultLayout(
                                                      LeadCreate,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/challenges/leads/:id`}
                                                    render={wrapInDefaultLayout(
                                                      LeadDetails,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/challenges/recommendations/create`}
                                                    render={wrapInDefaultLayout(
                                                      RecommendationDetails,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/challenges/recommendations/:id`}
                                                    render={wrapInDefaultLayout(
                                                      RecommendationDetails,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/challenges/:id`}
                                                    render={wrapInDefaultLayout(
                                                      ChallengeDetails,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/orders`}
                                                    render={wrapInDefaultLayout(
                                                      OrdersList,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/journey/:id`}
                                                    render={wrapInDefaultLayout(
                                                      JourneyOverall,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/survey/:id`}
                                                    render={wrapInDefaultLayout(
                                                      Survey,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/certificate/:id([0-9a-f]{24})`}
                                                    render={wrapInDefaultLayout(
                                                      Certificate,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/certificate/:code`}
                                                    render={wrapInDefaultLayout(
                                                      AcademyDetails,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/reports/academy/scorm`}
                                                    render={wrapInDefaultLayout(
                                                      ReportAcademyScorm,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/reports/perform`}
                                                    render={wrapInDefaultLayout(
                                                      ReportPerform,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/reports`}
                                                    render={wrapInDefaultLayout(
                                                      TeamReport,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/teams/structure`}
                                                    render={wrapInDefaultLayout(
                                                      TeamStructure,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/shop/order/:encodedOrg([0-9a-f]{48})/:token([0-9a-z]{36})`}
                                                    render={wrapInDefaultLayout(
                                                      ShopUserOrderPay,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    exact
                                                    path={`${match.url}/shop`}
                                                    render={wrapInDefaultLayout(
                                                      ShopSearch,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/shop/opportunities/create`}
                                                    render={wrapInDefaultLayout(
                                                      OrderCreate,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/shop/opportunities/:id/edit`}
                                                    render={wrapInDefaultLayout(
                                                      OrderCreate,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/shop/order/create`}
                                                    render={wrapInDefaultLayout(
                                                      OrderCreate,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/shop/product`}
                                                    render={wrapInDefaultLayout(
                                                      ShopProductPage,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/shop/checkout`}
                                                    render={wrapInDefaultLayout(
                                                      ShopCheckoutSteps,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/shop/order/:id/pay`}
                                                    render={wrapInDefaultLayout(
                                                      ShopUserOrderPay,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/shop/order/subscription/:id`}
                                                    render={wrapInDefaultLayout(
                                                      ShopUserSubscriptionsOrderPage,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/shop/opportunity/:id`}
                                                    render={wrapInDefaultLayout(
                                                      ShopUserOrderPage,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/shop/order/:id`}
                                                    render={wrapInDefaultLayout(
                                                      ShopUserOrderPage,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/shop/order`}
                                                    render={wrapInDefaultLayout(
                                                      ShopUserOrders,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/shop/opportunities`}
                                                    exact
                                                    render={wrapInDefaultLayout(
                                                      OpportunitiesList,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/shop/academy/order`}
                                                    render={wrapInDefaultLayout(
                                                      ShopUserOrders,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/shop/academy/:id`}
                                                    render={wrapInDefaultLayout(
                                                      ShopUserOrderPage,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/myplus/privacy`}
                                                    render={() => (
                                                      <DSKBankPrivacyPolicy
                                                        {...this.props}
                                                      />
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/market/opportunity/:encodedOrg/:token`}
                                                    render={wrapInDefaultLayout(
                                                      Opportunity,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    path={`${match.url}/user/:userPageName`}
                                                    render={wrapInDefaultLayout(
                                                      MarketUserPage,
                                                      this.props,
                                                    )}
                                                  />
                                                  <Route
                                                    exact
                                                    path={`${match.url}/`}
                                                    render={
                                                      isShopPage
                                                        ? () => (
                                                            <ShopMain
                                                              {...this.props}
                                                            />
                                                          )
                                                        : () => (
                                                            <LandingPage
                                                              {...this.props}
                                                            />
                                                          )
                                                    }
                                                  />
                                                  <Route
                                                    exact
                                                    path={`${match.url}*`}
                                                    render={() => (
                                                      <NotFound
                                                        {...this.props}
                                                      />
                                                    )}
                                                  />
                                                </Switch>
                                              )}
                                            />
                                          </Switch>
                                        )}
                                      </>
                                    </Title>
                                  </Elements>
                                </BadgeContextProvider>
                              </IsTestModeContext.Provider>
                            </WorldContext.Provider>
                          </DirectSellingModelContext.Provider>
                        </MarketInfosContext.Provider>
                      </LanguagePrefixContext.Provider>
                    </ServicesContext.Provider>
                  </MetaContext.Provider>
                </LoggedUserContext.Provider>
              </ConfigsContext.Provider>
            </ShopCartContext.Provider>
          </IsAcademyPublicContext.Provider>
        </CSRFContext.Provider>
      </TranslationContext.Provider>
    );
  }
}

Main.propTypes = {
  /** translation function, i18n.__ on the browser, response.__ on the server */
  t: PropTypes.func.isRequired,
  /** Cross site request forgery token, used on non-get requests to the server */
  _csrf: PropTypes.string,
  cookies: PropTypes.instanceOf(Cookies).isRequired,
  profile: PropTypes.shape(UserProp.propType),
  tenant: PropTypes.shape(TenantProp.propTypes),
  status: PropTypes.number,
  error: PropTypes.shape({}),
  stripe: PropTypes.shape({
    key: PropTypes.string.isRequired,
  }),
  orgServices: PropTypes.shape({
    connect: PropTypes.shape({
      enabled: PropTypes.bool,
      config: PropTypes.shape({
        usersEnabled: PropTypes.bool,
        groupsEnabled: PropTypes.bool,
      }),
    }),
  }),
  branding: PropTypes.shape(BrandingProp.propType),
  academyConfig: PropTypes.shape(AcademyConfigProp.propType).isRequired,
  tawkToKey: PropTypes.string.isRequired,
  favicon: PropTypes.string.isRequired,
  socketUrl: PropTypes.string.isRequired,
  languages: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  languagePrefix: PropTypes.string.isRequired,
  currencies: PropTypes.arrayOf(PropTypes.shape(CurrencyProp.propType)),
  countries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType)),
  meta: PropTypes.shape({
    appName: PropTypes.string.isRequired,
  }),
  share: PropTypes.shape({}),
  configs: PropTypes.shape(TenantConfigsProp.propType),
  publicModules: PropTypes.shape({
    marketPlace: PropTypes.bool,
    academy: PropTypes.bool,
  }),
  hasAcademyHeader: PropTypes.bool,
  isShopPage: PropTypes.bool,
  directSellingActive: PropTypes.bool,
  isSignIn: PropTypes.bool,
  marketInfosCookies: PropTypes.shape({
    localCurrency: PropTypes.string,
    localCountry: PropTypes.string,
    localLanguage: PropTypes.string,
  }).isRequired,
  directSellingModel: PropTypes.string,
  url: PropTypes.string.isRequired,
  userPortalMenus: PropTypes.arrayOf(
    PropTypes.shape(UserPortalMenusProp.propType),
  ).isRequired,
  organizationConfigs: PropTypes.shape({
    tenant: PropTypes.shape(TenantProp.propType),
    organization: PropTypes.shape(organizationProp.propType),
  }).isRequired,
  availableCurrencies: PropTypes.arrayOf(
    PropTypes.shape(CurrencyProp.propType),
  ),
  vatTypes: PropTypes.arrayOf(PropTypes.shape(VatTypesProp.propType)),
  availableCountries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType)),
  reloadAfterCookies: PropTypes.bool,
  isToOpenSponsorModal: PropTypes.bool,
  isTestMode: PropTypes.bool,
  isTrial: PropTypes.bool,
};

Main.defaultProps = {
  _csrf: '',
  profile: null,
  tenant: null,
  status: null,
  error: null,
  stripe: null,
  branding: null,
  orgServices: null,
  currencies: [],
  countries: [],
  meta: {},
  share: {},
  configs: {},
  publicModules: {
    marketPlace: false,
    academy: false,
  },
  hasAcademyHeader: false,
  isShopPage: false,
  directSellingActive: false,
  isSignIn: false,
  directSellingModel: null,
  availableCountries: [],
  availableCurrencies: [],
  vatTypes: [],
  reloadAfterCookies: false,
  isToOpenSponsorModal: false,
  isTestMode: false,
  isTrial: false,
};

export default withCookies(Main);
